// src/components/aiTalentDrive/JoinFlow.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "./api";
import "../../styles/hiring-circuit.css";
import "../../styles/drive-team-join.css";

/*
 * AI Talent Drive: a company adds its own recruiters.
 *
 * The person names their company, proves a WORK email with a one-time code,
 * and lists colleagues on the same email domain. On confirm each colleague
 * is invited to the drive's hiring dashboard as a Recruiter; someone who
 * already has access is sent straight to the dashboard, so the same form is
 * both sign-up and sign-in.
 *
 * Two places mount it: the token link Babblebots hands out
 * (/ai-talent-drive/join/<token>, pages/DriveTeamJoin.tsx) and, with the
 * token "public", the hiring-companies page, where the server resolves the
 * campaign staff flagged for the website.
 *
 * API: babblebots-monolith companyapp/hiring_circuit_views.py (join_*) and
 * companyapp/hiring_circuit_join.py for the rules the server enforces. The
 * client checks the same things first only so people get instant feedback;
 * the server's answer always wins.
 */

type Props = {
  token: string;
  /* Mounted inside another page (no page chrome, website wording) */
  embedded?: boolean;
  /* Called once access was set up: invites sent, or "just me" done */
  onSuccess?: () => void;
};

// Cloudflare Turnstile. Empty = widget not rendered; the backend skips the
// check too when its secret is unset, so local and staging work without keys.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const OTP_LENGTH = 4; // core.models.OtpMap codes are four digits
const RESEND_SECONDS = 30;
const REMEMBER_KEY = "bb_drive_join_remember";
const HELP_EMAIL = "helpme@babblebots.ai";
const DASHBOARD_URL = "https://app.babblebots.ai/ai-talent-drive";
const REDIRECT_SECONDS = 5;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^(\+?91|0)?[6-9]\d{9}$/;
const PERSONAL_DOMAINS = [
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.in", "yahoo.co.in", "ymail.com",
  "outlook.com", "hotmail.com", "live.com", "msn.com", "icloud.com", "me.com",
  "rediffmail.com", "rediff.com", "protonmail.com", "proton.me", "zoho.com", "aol.com",
];

// "error": the server could not be reached while checking the link; the
// page offers a retry rather than claiming the link is dead.
type LinkState = "loading" | "active" | "inactive" | "error";
type Step = "company" | "members" | "done";
type Member = {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  errors: Partial<Record<"first_name" | "last_name" | "phone" | "email", string>>;
  leaving?: boolean;
};
type CompanyErrors = Partial<Record<
  "company_name" | "your_first_name" | "your_last_name" | "email" | "members", string
>>;

const domainOf = (email: string) => email.trim().toLowerCase().split("@")[1] || "";
const emptyMember = (id: number): Member => ({
  id, first_name: "", last_name: "", phone: "", email: "", errors: {},
});

const readRemember = () => {
  try { return window.localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; }
};
const writeRemember = (token: string) => {
  try {
    if (token) window.localStorage.setItem(REMEMBER_KEY, token);
    else window.localStorage.removeItem(REMEMBER_KEY);
  } catch { /* private mode: the page still works, it just won't remember */ }
};

async function api(path: string, init?: RequestInit) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  let data: any = null;
  try { data = await res.json(); } catch { data = null; }
  return { ok: res.ok, status: res.status, data: data || {} };
}

/* ---------- Turnstile (optional) ---------- */

// Cloudflare's global, read through a cast rather than a `declare global`
// augmentation: two files declaring Window.turnstile with different shapes
// fail the build, and a stale copy of the old page did exactly that.
type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove?: (id: string) => void;
};
const turnstileApi = (): TurnstileApi | undefined =>
  (window as unknown as { turnstile?: TurnstileApi }).turnstile;

function useTurnstile(enabled: boolean) {
  const holder = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string>("");
  const [token, setToken] = useState("");

  useEffect(() => {
    if (!enabled || !TURNSTILE_SITE_KEY || !holder.current) return;
    let cancelled = false;
    const render = () => {
      const api = turnstileApi();
      if (cancelled || !api || !holder.current || widgetId.current) return;
      widgetId.current = api.render(holder.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        size: "normal",
        callback: (t: string) => setToken(t),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
    };
    if (turnstileApi()) render();
    else {
      let script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", render);
    }
    // The widget is a fixed 300px iframe. Where the form cell is narrower
    // (phones), scale the whole holder down to the cell's width instead of
    // letting it run past the card; clicks still land through a transform.
    const cell = holder.current.parentElement;
    const fit = () => {
      const el = holder.current;
      if (!el || !cell) return;
      const ratio = Math.min(1, cell.clientWidth / 300);
      el.style.transform = ratio < 1 ? `scale(${ratio})` : "";
      el.style.transformOrigin = "0 0";
      el.style.height = ratio < 1 ? `${Math.round(65 * ratio)}px` : "";
    };
    const watcher = typeof ResizeObserver !== "undefined" && cell ? new ResizeObserver(fit) : null;
    watcher?.observe(cell as Element);
    fit();
    return () => {
      cancelled = true;
      watcher?.disconnect();
      // The holder unmounts when the step changes ("Start over" included);
      // forget the widget so the next mount renders a fresh one.
      if (widgetId.current) {
        try { turnstileApi()?.remove?.(widgetId.current); } catch { /* already gone */ }
        widgetId.current = "";
        setToken("");
      }
    };
  }, [enabled]);

  const reset = useCallback(() => {
    setToken("");
    if (widgetId.current) turnstileApi()?.reset(widgetId.current);
  }, []);

  return { holder, token, reset, required: !!TURNSTILE_SITE_KEY };
}

/* ---------- page ---------- */

export default function JoinFlow({ token, embedded = false, onSuccess }: Props) {
  // Fired once per mount: "just me" followed by "add colleagues" is one
  // sign-up, not two.
  const successFired = useRef(false);
  const fireSuccess = () => {
    if (successFired.current) return;
    successFired.current = true;
    onSuccess?.();
  };

  const [link, setLink] = useState<LinkState>("loading");
  const [step, setStep] = useState<Step>("company");

  // company + submitter
  const [companyName, setCompanyName] = useState("");
  const [yourFirst, setYourFirst] = useState("");
  const [yourLast, setYourLast] = useState("");
  const [email, setEmail] = useState("");
  const [cErrors, setCErrors] = useState<CompanyErrors>({});
  // Company details lock once they were checked on the way to the code, or
  // came back prefilled for a remembered browser. A remembered browser that
  // never finished a submission has them empty, so they stay editable.
  const [detailsFrozen, setDetailsFrozen] = useState(false);
  const [sending, setSending] = useState(false);

  // one-time code
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState("");
  const [cells, setCells] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [otpState, setOtpState] = useState<"" | "bad" | "ok" | "busy">("");
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [remember, setRemember] = useState(true);
  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);

  // verified identity
  const [session, setSession] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const domain = domainOf(verifiedEmail);

  // members
  const nextId = useRef(1);
  const [members, setMembers] = useState<Member[]>([]);
  const [honeypot, setHoneypot] = useState("");
  const [banner, setBanner] = useState<React.ReactNode>(null);
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ invited: number; company: string; list: Member[] } | null>(null);
  // After verification: invite colleagues (rows) or just themselves (one
  // invite, then the dashboard link). An email that already has access is
  // never re-invited or pre-filled.
  const [inviteChoice, setInviteChoice] = useState<null | "yes" | "no">(null);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [selfBusy, setSelfBusy] = useState(false);
  const [selfInvited, setSelfInvited] = useState(false);

  const turnstile = useTurnstile(link === "active" && step === "company");

  /* ----- the link ----- */
  const [linkAttempt, setLinkAttempt] = useState(0);
  const retryLink = () => { setLink("loading"); setLinkAttempt((n) => n + 1); };
  useEffect(() => {
    let alive = true;
    (async () => {
      const remembered = readRemember();
      const qs = remembered ? `?remember=${encodeURIComponent(remembered)}` : "";
      try {
        const { ok, data } = await api(`/hiring-circuit/join/${encodeURIComponent(token)}/${qs}`);
        if (!alive) return;
        if (!ok || !data.active) { setLink("inactive"); return; }
        if (data.remembered && data.session) {
          // This browser verified a work email on this link recently:
          // straight to members, company prefilled from last time.
          const r = data.remembered;
          setCompanyName(r.company_name || "");
          const [first = "", ...rest] = String(r.your_name || "").split(" ");
          setYourFirst(first);
          setYourLast(rest.join(" "));
          setDetailsFrozen(!!(r.company_name && r.your_name));
          setEmail(r.email || "");
          setVerifiedEmail(r.email || "");
          setAlreadyMember(!!r.already_member);
          // Someone who already has access isn't asked: straight to "all set".
          setInviteChoice(r.already_member ? "no" : null);
          setSession(data.session);
          setStep("members");
        }
        setLink("active");
      } catch {
        if (alive) setLink("error");
      }
    })();
    return () => { alive = false; };
  }, [token, linkAttempt]);

  /* ----- resend countdown ----- */
  useEffect(() => {
    if (countdown <= 0) return;
    const t = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [countdown]);

  // Once invites are out, the page counts down and lands on the hiring
  // dashboard by itself. Only the "invites on the way" screen does this.
  const [redirectIn, setRedirectIn] = useState<number | null>(null);
  const goToDashboard = () => { window.location.assign(DASHBOARD_URL); };
  useEffect(() => {
    if (redirectIn === null) return;
    if (redirectIn <= 0) { goToDashboard(); return; }
    const t = window.setTimeout(() => setRedirectIn((n) => (n === null ? null : n - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [redirectIn]);

  const detailErrors = () => {
    const errs: CompanyErrors = {};
    if (!companyName.trim()) errs.company_name = "Enter your company name.";
    if (!yourFirst.trim()) errs.your_first_name = "Enter your first name.";
    if (!yourLast.trim()) errs.your_last_name = "Enter your last name.";
    return errs;
  };

  /* ----- step 1: send the code ----- */
  const sendCode = async () => {
    const errs: CompanyErrors = detailErrors();
    const em = email.trim().toLowerCase();
    if (!EMAIL_RE.test(em)) errs.email = "Enter a valid work email.";
    else if (PERSONAL_DOMAINS.includes(domainOf(em))) errs.email = "Use your company email, not a personal one.";
    if (turnstile.required && !turnstile.token) errs.email = errs.email || "Please complete the human check below.";
    setCErrors(errs);
    if (Object.keys(errs).length) return;

    setSending(true);
    setBanner(null);
    try {
      const { ok, status, data } = await api("/hiring-circuit/join/otp/", {
        method: "POST",
        body: JSON.stringify({ token, email: em, turnstile_token: turnstile.token }),
      });
      if (status === 404) { setLink("inactive"); return; }
      if (!ok) {
        const f = data.fields || {};
        setCErrors({ email: f.email || f.turnstile || data.message || "Couldn't send the code. Try again." });
        if (f.turnstile) turnstile.reset();
        return;
      }
      setOtpSentTo(em);
      setOtpOpen(true);
      setCells(Array(OTP_LENGTH).fill(""));
      setOtpState("");
      setOtpError("");
      setCountdown(RESEND_SECONDS);
      turnstile.reset();
      window.setTimeout(() => cellRefs.current[0]?.focus(), 50);
    } catch {
      setCErrors({ email: "Couldn't reach the server. Check your connection and try again." });
    } finally {
      setSending(false);
    }
  };

  /* ----- step 1b: verify the code ----- */
  const verifyCode = useCallback(async (code: string) => {
    setOtpState("busy");
    setOtpError("");
    try {
      const { ok, status, data } = await api("/hiring-circuit/join/otp/verify/", {
        method: "POST",
        body: JSON.stringify({ token, email: otpSentTo, code, remember }),
      });
      if (status === 404) { setLink("inactive"); return; }
      if (!ok) {
        setOtpState("bad");
        setOtpError(data.fields?.code || data.fields?.email || "That code isn't right or has expired.");
        window.setTimeout(() => {
          setCells(Array(OTP_LENGTH).fill(""));
          cellRefs.current[0]?.focus();
        }, 350);
        return;
      }
      setOtpState("ok");
      writeRemember(remember ? data.remember || "" : "");
      setDetailsFrozen(true);
      window.setTimeout(() => {
        setSession(data.session);
        setVerifiedEmail(data.email);
        setAlreadyMember(!!data.already_member);
        setInviteChoice(data.already_member ? "no" : null);
        setOtpOpen(false);
        setStep("members");
      }, 350);
    } catch {
      setOtpState("bad");
      setOtpError("Couldn't reach the server. Try again.");
    }
  }, [token, otpSentTo, remember]);

  const setCell = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      setCells((c) => { const n = [...c]; n[index] = ""; return n; });
      return;
    }
    // Typing or pasting several digits fills forward from this cell.
    const next = [...cells];
    for (let i = 0; i < digits.length && index + i < OTP_LENGTH; i++) next[index + i] = digits[i];
    setCells(next);
    setOtpState("");
    setOtpError("");
    const last = Math.min(index + digits.length, OTP_LENGTH - 1);
    cellRefs.current[last]?.focus();
    if (next.every((d) => d)) verifyCode(next.join(""));
  };

  const onCellKey = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !cells[index] && index > 0) {
      setCells((c) => { const n = [...c]; n[index - 1] = ""; return n; });
      cellRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) cellRefs.current[index - 1]?.focus();
    else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) cellRefs.current[index + 1]?.focus();
  };

  const startOver = () => {
    writeRemember("");
    setSession("");
    setVerifiedEmail("");
    setOtpOpen(false);
    setMembers([]);
    setBanner(null);
    setDone(null);
    setRedirectIn(null);
    setDetailsFrozen(false);
    setInviteChoice(null);
    setAlreadyMember(false);
    setSelfInvited(false);
    setStep("company");
  };

  const selfRow = (): Member => ({
    id: nextId.current++, first_name: yourFirst.trim(), last_name: yourLast.trim(),
    phone: "", email: verifiedEmail, errors: {},
  });

  const chooseYes = () => {
    setBanner(null);
    setRedirectIn(null);
    setInviteChoice("yes");
    // The verifier gets access too unless they already have it; the row is
    // theirs to remove.
    setMembers(alreadyMember || selfInvited ? [emptyMember(nextId.current++)] : [selfRow(), emptyMember(nextId.current++)]);
    window.setTimeout(() => document.querySelector<HTMLInputElement>(".tj-member input")?.focus(), 30);
  };

  const chooseNo = async () => {
    setBanner(null);
    if (alreadyMember || selfInvited) { setInviteChoice("no"); return; }
    const errs = detailErrors();
    if (Object.keys(errs).length) {
      setCErrors((c) => ({ ...c, ...errs }));
      window.setTimeout(() => document.querySelector<HTMLInputElement>('.tj-company [aria-invalid="true"]')?.focus(), 0);
      return;
    }
    setSelfBusy(true);
    try {
      const data = await sendInvites([selfRow()], (rows) => {
        const first = Object.values(rows)[0] as Record<string, string> | undefined;
        setBanner(first ? Object.values(first)[0] : "Couldn't set up your access. Try again.");
      });
      if (data) { setSelfInvited(true); setInviteChoice("no"); fireSuccess(); }
    } finally {
      setSelfBusy(false);
    }
  };

  /* ----- step 2: members ----- */
  const updateMember = (id: number, key: keyof Member, value: string) =>
    setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, [key]: value, errors: { ...m.errors, [key]: undefined } } : m)));

  const addMember = () => {
    const id = nextId.current++;
    setMembers((ms) => [...ms, emptyMember(id)]);
    window.setTimeout(() => document.getElementById(`tj-first-${id}`)?.focus(), 30);
  };

  const removeMember = (id: number) => {
    if (members.length === 1) {
      setMembers([emptyMember(nextId.current++)]);
      return;
    }
    setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, leaving: true } : m)));
    window.setTimeout(() => setMembers((ms) => ms.filter((m) => m.id !== id)), 240);
  };

  const validateMembers = () => {
    let ok = true;
    const seen = new Set<string>();
    const checked = members.map((m) => {
      const errors: Member["errors"] = {};
      const em = m.email.trim().toLowerCase();
      if (!m.first_name.trim()) errors.first_name = "Enter a first name.";
      if (!m.last_name.trim()) errors.last_name = "Enter a last name.";
      if (!PHONE_RE.test(m.phone.replace(/[\s-]/g, ""))) errors.phone = "Enter a valid 10-digit Indian mobile.";
      if (!EMAIL_RE.test(em)) errors.email = "Enter a valid work email.";
      else if (domainOf(em) !== domain) errors.email = `Only @${domain} addresses can be added.`;
      else if (seen.has(em)) errors.email = "This email is already in the list.";
      seen.add(em);
      if (Object.keys(errors).length) ok = false;
      return { ...m, errors };
    });
    setMembers(checked);
    return ok;
  };

  const openReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // a bot filled the hidden field
    setBanner(null);
    const errs = detailErrors();
    const membersOk = validateMembers();
    if (Object.keys(errs).length) {
      setCErrors((c) => ({ ...c, ...errs }));
      window.setTimeout(() => document.querySelector<HTMLInputElement>('.tj-company [aria-invalid="true"]')?.focus(), 0);
      return;
    }
    if (!membersOk) {
      window.setTimeout(() => document.querySelector<HTMLInputElement>('.tj-member [aria-invalid="true"]')?.focus(), 0);
      return;
    }
    setReviewing(true);
  };

  // POSTs the invites; returns the response data on success, null after
  // having shown the failure (field errors, expired session, refusal).
  const sendInvites = async (list: Member[], onRowErrors: (rows: Record<string, unknown>) => void) => {
    try {
      const { ok, status, data } = await api("/hiring-circuit/join/members/", {
        method: "POST",
        body: JSON.stringify({
          token, session,
          company_name: companyName.trim(),
          your_name: `${yourFirst.trim()} ${yourLast.trim()}`.trim(),
          members: list.map((m) => ({
            first_name: m.first_name.trim(), last_name: m.last_name.trim(),
            phone: m.phone.trim(), email: m.email.trim().toLowerCase(),
          })),
        }),
      });
      if (status === 404) { setLink("inactive"); return null; }
      if (status === 401) {
        // The 30-minute verification lapsed: back to the code, inputs kept.
        setSession("");
        setVerifiedEmail("");
        setInviteChoice(null);
        setStep("company");
        setCErrors({ email: "Your verification expired. Send a new code to continue." });
        return null;
      }
      if (status === 409) {
        setBanner(<>{data.message || "This company is already registered under a different email domain."}{" "}Write to <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a> and we'll add you.</>);
        return null;
      }
      if (!ok) {
        const f = data.fields || {};
        const rows = data.members || {};
        // Company-detail errors show on their fields (still editable on this
        // step); the verified email and session are untouched.
        setCErrors((c) => ({ ...c, company_name: f.company_name, your_first_name: f.your_name }));
        if (f.company_name || f.your_name) setDetailsFrozen(false);
        if (Object.keys(rows).length) onRowErrors(rows);
        else if (!f.company_name && !f.your_name) setBanner(f.members || data.message || "Something wasn't right. Check the form and try again.");
        return null;
      }
      return data;
    } catch {
      setBanner("Couldn't reach the server. Nothing was sent — try again in a moment.");
      return null;
    }
  };

  const confirm = async () => {
    setSubmitting(true);
    const data = await sendInvites(members, (rows) =>
      setMembers((ms) => ms.map((m, i) => ({ ...m, errors: (rows[String(i)] as Member["errors"]) || {} }))));
    setSubmitting(false);
    setReviewing(false);
    if (!data) return;
    setDone({ invited: data.invited, company: data.company || companyName.trim(), list: members });
    setStep("done");
    fireSuccess();
    setRedirectIn(REDIRECT_SECONDS);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------- render ---------- */

  const stepIndex =
    step === "company" ? 1
    : step === "members" ? (inviteChoice === "no" ? 4 : reviewing ? 3 : 2)
    : 4;
  const stepState = (n: number) => (n < stepIndex ? "done" : n === stepIndex ? "current" : "todo");
  const STEPS = ["Your company", "Add members", "Review", "Invites sent"];
  const companyLocked = step !== "company";          // email verified: members unlocked
  const detailsLocked = companyLocked && detailsFrozen; // company/name fields read-only
  // Beside the email on the token page; at the end of the row on the
  // companies page, where the fields sit in one line.
  const otpButton = otpOpen ? (
    <button type="button" className="tj-vbtn" onClick={() => { setOtpOpen(false); setCountdown(0); }}>Change</button>
  ) : (
    <button type="button" className="tj-vbtn" onClick={sendCode} disabled={sending}>
      {sending ? "Sending…" : "Send OTP"}
    </button>
  );

  return (
    <>
        <div className="tj-page" id={embedded ? "signup" : undefined}>
          {link === "loading" && (
            <div className="tj-loading" role="status"><i />{embedded ? "Loading sign-in" : "Checking your link"}</div>
          )}

          {link === "error" && (
            <div className="tj-expired" role="alert">
              <span className="tj-ring">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
              </span>
              <h2>Couldn't reach the server</h2>
              <p>Check your connection and try again. If it keeps failing, write to <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>.</p>
              <div className="tj-actions" style={{ justifyContent: "center", marginTop: 6 }}>
                <button type="button" className="tj-cta" onClick={retryLink}>Try again</button>
              </div>
            </div>
          )}

          {link === "inactive" && (
            <div className="tj-expired">
              <span className="tj-ring">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v6l4 2" /></svg>
              </span>
              <h2>{embedded ? "Company sign-up is paused right now" : "This link is no longer active"}</h2>
              <p>
                {embedded
                  ? <>Write to <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a> and we'll set up your recruiter access by hand.</>
                  : <>Babblebots rotates the team sign-up link from time to time. Ask your Babblebots
                    contact for the current one, or write to <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>.</>}
              </p>
            </div>
          )}

          {link === "active" && step !== "done" && (
            <>
              {!embedded && (
              <ol className="tj-steps" aria-label="Progress">
                {STEPS.map((name, i) => (
                  <li key={name} data-state={stepState(i + 1)} aria-current={stepState(i + 1) === "current" ? "step" : undefined}>
                    <b>
                      {stepState(i + 1) === "done" ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg>
                      ) : (
                        i + 1
                      )}
                    </b>
                    <span>{name}</span>
                  </li>
                ))}
              </ol>
              )}
              {embedded ? (
                <>
                  {/* h2: the companies page's own hero holds the page's h1 */}
                  <h2 className="tj-title">Instant FREE Access:<br /><em>sign&#8209;up</em> to see the talent pool!</h2>
                  <p className="tj-lede">Watch candidate video interviews, download CVs.</p>
                </>
              ) : (
                <>
                  <div className="tj-eyebrow">AI Talent Drive · Hiring team access</div>
                  <h1>Bring your hiring team into the <em>AI Talent Drive</em></h1>
                </>
              )}

              {/* ----- step 1: company + submitter ----- */}
              <div className={`tj-company${embedded ? " tj-company--row" : ""}`} style={{ marginTop: 26 }}>
                <div className="tj-field">
                  <label htmlFor="tj-company">Company name</label>
                  <input id="tj-company" value={companyName} readOnly={detailsLocked} autoComplete="organization"
                    placeholder="Company Name" aria-invalid={!!cErrors.company_name}
                    onChange={(e) => { setCompanyName(e.target.value); setCErrors((c) => ({ ...c, company_name: undefined })); }} />
                  <span className="tj-err">{cErrors.company_name}</span>
                </div>
                <div className="tj-field">
                  <label htmlFor="tj-email">Work email</label>
                  <div className="tj-vrow">
                    <input id="tj-email" type="email" inputMode="email" autoComplete="email" value={email}
                      readOnly={companyLocked || otpOpen} placeholder="name@company.com"
                      aria-invalid={!!cErrors.email}
                      onChange={(e) => { setEmail(e.target.value); setCErrors((c) => ({ ...c, email: undefined })); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && !otpOpen) { e.preventDefault(); sendCode(); } }} />
                    {!companyLocked && !embedded && otpButton}
                  </div>
                  <span className="tj-err">{cErrors.email}</span>
                </div>
                <div className="tj-field">
                  <label htmlFor="tj-first">First Name</label>
                  <input id="tj-first" value={yourFirst} readOnly={detailsLocked} autoComplete="given-name"
                    placeholder="First Name" aria-invalid={!!cErrors.your_first_name}
                    onChange={(e) => { setYourFirst(e.target.value); setCErrors((c) => ({ ...c, your_first_name: undefined })); }} />
                  <span className="tj-err">{cErrors.your_first_name}</span>
                </div>
                <div className="tj-field">
                  <label htmlFor="tj-last">Last Name</label>
                  <input id="tj-last" value={yourLast} readOnly={detailsLocked} autoComplete="family-name"
                    placeholder="Last Name" aria-invalid={!!cErrors.your_last_name}
                    onChange={(e) => { setYourLast(e.target.value); setCErrors((c) => ({ ...c, your_last_name: undefined })); }} />
                  <span className="tj-err">{cErrors.your_last_name}</span>
                </div>
                {embedded && !companyLocked && (
                  <div className="tj-field tj-otpcol">
                    <label>OTP verification</label>
                    {otpButton}
                    <span className="tj-err" />
                  </div>
                )}

                {!companyLocked && turnstile.required && (
                  <div className="tj-span2"><div className="tj-turnstile" ref={turnstile.holder} /></div>
                )}

                {otpOpen && !companyLocked && (
                  <div className="tj-otp">
                    <div className="tj-otp-head">
                      <span className="tj-otp-ic">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
                      </span>
                      <div>
                        <div className="tj-otp-title">Check your inbox</div>
                        <span className="tj-otp-txt">
                          We sent a {OTP_LENGTH}-digit OTP to <b>{otpSentTo}</b>. It expires in 10 minutes.
                        </span>
                      </div>
                    </div>
                    <div className="tj-cells" data-state={otpState} role="group" aria-label="One-time code">
                      {cells.map((v, i) => (
                        <input key={i} ref={(el) => { cellRefs.current[i] = el; }} value={v}
                          inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"}
                          aria-label={`Digit ${i + 1}`} className={v ? "filled" : ""}
                          disabled={otpState === "busy" || otpState === "ok"}
                          onChange={(e) => setCell(i, e.target.value)}
                          onKeyDown={(e) => onCellKey(i, e)}
                          onFocus={(e) => e.target.select()} />
                      ))}
                    </div>
                    <div className="tj-otp-foot">
                      <span className="tj-err" role="alert">{otpError}</span>
                      <span className="tj-resend">
                        Didn't get it?{" "}
                        <button type="button" disabled={countdown > 0 || sending} onClick={sendCode}>
                          {countdown > 0 ? <>Resend in <b>{countdown}</b>s</> : "Resend OTP"}
                        </button>
                      </span>
                    </div>
                    <label className="tj-remember">
                      <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                      Remember this browser for 30 days
                    </label>
                  </div>
                )}

                {companyLocked && (
                  <div className="tj-verified">
                    <span className="tj-vtext">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 12l5 5L20 7" /></svg>
                      <span>
                        <b>{verifiedEmail}</b> verified · colleagues must use your company's email domain
                        (<b>@{domain}</b>); each gets their login email the moment you confirm.
                      </span>
                    </span>
                    <button type="button" className="tj-link" onClick={startOver}>Not you? Start over</button>
                  </div>
                )}
              </div>

              {/* ----- after verification: colleagues, or just me? ----- */}
              {companyLocked && inviteChoice === null && (
                <div className="tj-ask">
                  <div>
                    <div className="tj-ask-title">Do you want to invite your colleagues?</div>
                    <p className="tj-ask-txt">
                      Add the people on your hiring team now — each gets a Recruiter login. Or set up
                      just your own access and add others later.
                    </p>
                  </div>
                  <div className="tj-ask-btns">
                    <button type="button" className="tj-cta" onClick={chooseYes} disabled={selfBusy}>
                      Yes, add colleagues
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </button>
                    <button type="button" className="tj-btn2" onClick={chooseNo} disabled={selfBusy}>
                      {selfBusy ? "Setting you up…" : "No, just me"}
                    </button>
                  </div>
                  {banner && (
                    <div className="tj-banner" role="alert">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                      <span>{banner}</span>
                    </div>
                  )}
                </div>
              )}

              {companyLocked && inviteChoice === "no" && (
                <div className="tj-set">
                  <span className="tj-ring tj-ring-sm">
                    <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  </span>
                  <h2>You're all set</h2>
                  <p>
                    {selfInvited
                      ? <>We've emailed <b>{verifiedEmail}</b> an invite. Open it, or go straight to the hiring dashboard and sign in with this email.</>
                      : <>Sign in to the hiring dashboard with <b>{verifiedEmail}</b>.</>}
                  </p>
                  <div className="tj-actions" style={{ justifyContent: "center", marginTop: 6 }}>
                    <a className="tj-cta" href={DASHBOARD_URL}>
                      Open the hiring dashboard
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M7 17L17 7M9 7h8v8" /></svg>
                    </a>
                    <button type="button" className="tj-ghost" onClick={chooseYes}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg>
                      Add colleagues
                    </button>
                  </div>
                </div>
              )}

              {/* ----- step 2: members ----- */}
              <form onSubmit={openReview} noValidate>
                {!companyLocked && (
                  <div className="tj-locked">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                    Verify your work email above to add members.
                  </div>
                )}

                {companyLocked && inviteChoice === "yes" && (
                  <>
                    <div className="tj-roster">
                      {members.map((m) => (
                        <div className={`tj-member${m.leaving ? " leaving" : ""}`} key={m.id}>
                          <div className="tj-field">
                            <label htmlFor={`tj-first-${m.id}`}>First name</label>
                            <input id={`tj-first-${m.id}`} value={m.first_name} autoComplete="given-name" placeholder="Anita"
                              aria-invalid={!!m.errors.first_name} onChange={(e) => updateMember(m.id, "first_name", e.target.value)} />
                            <span className="tj-err">{m.errors.first_name}</span>
                          </div>
                          <div className="tj-field">
                            <label htmlFor={`tj-last-${m.id}`}>Last name</label>
                            <input id={`tj-last-${m.id}`} value={m.last_name} autoComplete="family-name" placeholder="Rao"
                              aria-invalid={!!m.errors.last_name} onChange={(e) => updateMember(m.id, "last_name", e.target.value)} />
                            <span className="tj-err">{m.errors.last_name}</span>
                          </div>
                          <div className="tj-field">
                            <label htmlFor={`tj-phone-${m.id}`}>Mobile</label>
                            <input id={`tj-phone-${m.id}`} value={m.phone} inputMode="tel" autoComplete="tel" placeholder="+91 98XXX XXXXX"
                              aria-invalid={!!m.errors.phone} onChange={(e) => updateMember(m.id, "phone", e.target.value)} />
                            <span className="tj-err">{m.errors.phone}</span>
                          </div>
                          <div className="tj-field tj-wide">
                            <label htmlFor={`tj-mail-${m.id}`}>Work email</label>
                            <input id={`tj-mail-${m.id}`} value={m.email} type="email" inputMode="email" autoComplete="email"
                              placeholder={`anita@${domain}`} aria-invalid={!!m.errors.email}
                              onChange={(e) => updateMember(m.id, "email", e.target.value)} />
                            <span className="tj-err">{m.errors.email}</span>
                          </div>
                          <div className="tj-access">
                            <label>Access</label>
                            <span className="tj-pill">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                              Recruiter
                            </span>
                            <span className="tj-sp" />
                          </div>
                          <div className="tj-rm">
                            <label>Remove</label>
                            <button type="button" className="tj-remove" aria-label="Remove this member" onClick={() => removeMember(m.id)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6L6 18" /></svg>
                            </button>
                            <span className="tj-sp" />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="tj-roster-foot">
                      <button type="button" className="tj-ghost" onClick={addMember}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg>
                        Add another member
                      </button>
                      <span className="tj-count">{members.length} member{members.length === 1 ? "" : "s"}</span>
                    </div>

                    {/* honeypot: real users never see this; bots fill it */}
                    <div className="tj-hp" aria-hidden="true">
                      <label>Website <input type="text" name="website_url" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} /></label>
                    </div>

                    {banner && (
                      <div className="tj-banner" role="alert">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                        <span>{banner}</span>
                      </div>
                    )}

                    <div className="tj-actions">
                      <button type="submit" className="tj-cta">
                        Review &amp; send invites
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                        <span className="tj-bar" />
                      </button>
                      <span className="tj-note">Nothing is sent until you confirm on the next step.</span>
                    </div>
                  </>
                )}
              </form>
            </>
          )}

          {/* ----- step 4: done ----- */}
          {link === "active" && step === "done" && done && (
            <div className="tj-done">
              <span className="tj-ring">
                <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
              </span>
              <h2>{done.invited} Email invite{done.invited === 1 ? "" : "s"} on the way</h2>
              <div className="tj-actions" style={{ justifyContent: "center", marginTop: 18 }}>
                <button type="button" className="tj-cta" onClick={goToDashboard}>
                  Going to AI Talent Drive{redirectIn !== null ? ` (${redirectIn}s)` : ""}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </button>
              </div>
            </div>
          )}
        </div>

      {/* ----- step 3: review sheet ----- */}
      {reviewing && (
        <div className="tj-sheet-bg" onClick={(e) => { if (e.target === e.currentTarget && !submitting) setReviewing(false); }}>
          <div className="tj-sheet" role="dialog" aria-modal="true" aria-labelledby="tj-sheet-title">
            <h3 id="tj-sheet-title">
              Send {members.length} invite{members.length === 1 ? "" : "s"} for {companyName.trim()}?
            </h3>
            <p className="tj-sub">
              Each person gets an email from Babblebots with their Recruiter login. You can't undo a
              sent invite, but Babblebots can remove a member later.
            </p>
            <table>
              <thead><tr><th>Name</th><th>Contact</th><th>Access</th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td><b>{m.first_name.trim()} {m.last_name.trim()}</b></td>
                    <td><div className="tj-m">{m.email.trim().toLowerCase()}</div><div className="tj-m">{m.phone.trim()}</div></td>
                    <td><span className="tj-tag">Recruiter</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tj-foot">
              <button type="button" className="tj-btn2" onClick={() => setReviewing(false)} disabled={submitting}>Go back</button>
              <button type="button" className={`tj-cta${submitting ? " busy" : ""}`} onClick={confirm} disabled={submitting}>
                {submitting ? "Sending…" : "Confirm & send"}
                <span className="tj-bar" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
